import React, { useEffect, useRef, useState } from "react";
import { NODE_COLORS } from "../services/api";

// Lightweight force-directed layout rendered in SVG with animated entrance
// and a flowing highlight along the reasoning path.
export default function GraphView({ nodes, edges, highlight = [], onNodeClick }) {
  const W = 1100, H = 620;
  const [pos, setPos] = useState({});
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState(null);
  const raf = useRef();

  useEffect(() => {
    if (!nodes?.length) return;
    // init positions on a circle
    const p = {};
    nodes.forEach((n, i) => {
      const a = (i / nodes.length) * Math.PI * 2;
      p[n.id] = { x: W / 2 + Math.cos(a) * 220 + (Math.random() - 0.5) * 40,
                  y: H / 2 + Math.sin(a) * 180 + (Math.random() - 0.5) * 40, vx: 0, vy: 0 };
    });
    const idset = new Set(nodes.map((n) => n.id));
    const links = edges.filter((e) => idset.has(e.source) && idset.has(e.target));
    let ticks = 0;
    setReady(false);

    const step = () => {
      ticks++;
      // repulsion
      const arr = nodes;
      for (let i = 0; i < arr.length; i++) {
        const a = p[arr[i].id];
        for (let j = i + 1; j < arr.length; j++) {
          const b = p[arr[j].id];
          let dx = a.x - b.x, dy = a.y - b.y;
          let d2 = dx * dx + dy * dy || 1;
          const f = 9000 / d2;
          const d = Math.sqrt(d2);
          const ux = dx / d, uy = dy / d;
          a.vx += ux * f; a.vy += uy * f;
          b.vx -= ux * f; b.vy -= uy * f;
        }
      }
      // springs
      links.forEach((l) => {
        const a = p[l.source], b = p[l.target];
        let dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const f = (d - 150) * 0.02;
        const ux = dx / d, uy = dy / d;
        a.vx += ux * f; a.vy += uy * f;
        b.vx -= ux * f; b.vy -= uy * f;
      });
      // center gravity + integrate
      nodes.forEach((n) => {
        const a = p[n.id];
        a.vx += (W / 2 - a.x) * 0.008;
        a.vy += (H / 2 - a.y) * 0.008;
        a.vx *= 0.82; a.vy *= 0.82;
        a.x += a.vx; a.y += a.vy;
        a.x = Math.max(40, Math.min(W - 40, a.x));
        a.y = Math.max(40, Math.min(H - 40, a.y));
      });
      setPos({ ...p });
      if (ticks < 160) raf.current = requestAnimationFrame(step);
      else setReady(true);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [nodes, edges]);

  if (!nodes?.length) return null;
  const hset = new Set(highlight);
  const isHi = (a, b) => hset.has(a) && hset.has(b);
  const neighborsOf = hover ? new Set(edges.filter((e) => e.source === hover || e.target === hover).flatMap((e) => [e.source, e.target])) : null;

  return (
    <svg className={`graph-svg ${ready ? "ready" : ""}`} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
          <path d="M0,0 L7,3 L0,6" fill="#cbd5e1" />
        </marker>
      </defs>
      {edges.map((e, i) => {
        const a = pos[e.source], b = pos[e.target];
        if (!a || !b) return null;
        const hi = isHi(e.source, e.target);
        const dim = neighborsOf && !(neighborsOf.has(e.source) && neighborsOf.has(e.target));
        return (
          <g key={i} opacity={dim ? 0.12 : 1}>
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              className={`edge ${hi ? "hi" : ""}`} markerEnd="url(#arrow)" />
            {hi && <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="edge-flow" />}
          </g>
        );
      })}
      {nodes.map((n) => {
        const pt = pos[n.id];
        if (!pt) return null;
        const hi = hset.has(n.id);
        const dim = neighborsOf && !neighborsOf.has(n.id) && hover !== n.id;
        const c = NODE_COLORS[n.type] || "#64748b";
        const r = n.type === "objective" ? 16 : n.type === "dealer" || n.type === "sku" ? 13 : 11;
        return (
          <g key={n.id} transform={`translate(${pt.x},${pt.y})`} opacity={dim ? 0.2 : 1}
            className="node" onClick={() => onNodeClick?.(n.id)}
            onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}>
            {hi && <circle r={r + 6} className="node-halo" style={{ fill: c }} />}
            <circle r={r} style={{ fill: c }} className="node-dot" />
            <text y={r + 13} className="node-label">{n.label.length > 22 ? n.label.slice(0, 20) + "…" : n.label}</text>
          </g>
        );
      })}
    </svg>
  );
}
